import axios from '../AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';

/**
 * Chart roles — the tenant's semantic posting defaults, resolved by the
 * backend against the tenant's own chart version (the client never chooses a
 * chart version, and never hardcodes an account number as a fallback).
 *
 * Components that used to pin 1930/1630/2440/1460 now ask for a role here.
 * An unresolved role is an actionable state (link to the role settings under
 * Bokföring), never a silently substituted number.
 */

// One in-flight/settled fetch per company, revalidated after a short TTL —
// a settled result must never live for the whole SPA session (another user's
// confirm/archive would otherwise go unseen). Chart mutations (role confirm,
// account archive/edit/create) additionally invalidate explicitly.
export const ROLES_CACHE_TTL_MS = 60 * 1000;
const rolesCache = new Map(); // companyId -> { request, fetchedAt }

export async function fetchChartRoles(companyId, { force = false } = {}) {
    if (!companyId) throw new Error('companyId is required');
    const cached = rolesCache.get(companyId);
    const fresh = cached && (Date.now() - cached.fetchedAt) < ROLES_CACHE_TTL_MS;
    if (force || !fresh) {
        const request = axios
            .get(getApiUrl(`${companyId}/accounting/chart/roles`), { withCredentials: true })
            .then((response) => response.data.data)
            .catch((error) => {
                // A failed fetch must not poison the cache permanently.
                rolesCache.delete(companyId);
                throw error;
            });
        rolesCache.set(companyId, { request, fetchedAt: Date.now() });
        return request;
    }
    return cached.request;
}

export function invalidateChartRoles(companyId) {
    rolesCache.delete(companyId);
}

export async function confirmChartRole(companyId, role, accountNumber) {
    const response = await axios.post(
        getApiUrl(`${companyId}/accounting/chart/roles/${encodeURIComponent(role)}/confirm`),
        { accountNumber },
        { withCredentials: true }
    );
    invalidateChartRoles(companyId);
    return response.data.data;
}

/**
 * Resolve one role to `{ accountNumber, accountName }`.
 *
 * Returns `null` when the role is not resolved on this tenant (needs-mapping,
 * unsupported, or the roles endpoint failed) — the caller decides how to
 * surface that. It NEVER falls back to a hardcoded number.
 */
export async function resolveRoleAccount(companyId, role) {
    try {
        const data = await fetchChartRoles(companyId);
        const entry = (data.roles || []).find((item) => item.role === role);
        if (entry && entry.status === 'resolved') {
            return {
                accountId: entry.accountId,
                accountNumber: entry.accountNumber,
                accountName: entry.accountName || '',
            };
        }
        return null;
    } catch (error) {
        return null;
    }
}

/**
 * Resolve several roles at once: `{ [role]: {accountNumber, accountName} | null }`.
 */
export async function resolveRoleAccounts(companyId, roles) {
    const result = {};
    let data = null;
    try {
        data = await fetchChartRoles(companyId);
    } catch (error) {
        data = null;
    }
    for (const role of roles) {
        const entry = data ? (data.roles || []).find((item) => item.role === role) : null;
        result[role] = entry && entry.status === 'resolved'
            ? {
                accountId: entry.accountId,
                accountNumber: entry.accountNumber,
                accountName: entry.accountName || '',
            }
            : null;
    }
    return result;
}

/**
 * The tenant's payment accounts (betalkonton) from its own chart —
 * `is_payment_account`-flagged rows, falling back to the structural bank
 * range 19xx when the tenant has flagged none. Replaces every hardcoded
 * betalkonto `<option>` list.
 */
export async function fetchPaymentAccounts(companyId) {
    const response = await axios.get(
        getApiUrl(`${companyId}/accounting/accounts`),
        { withCredentials: true }
    );
    const accounts = response.data.accounts || [];
    const flagged = accounts.filter((account) => Number(account.is_payment_account) === 1);
    const candidates = flagged.length > 0 ? flagged : accounts.filter((account) => {
        const number = parseInt(account.account_number, 10);
        return number >= 1900 && number < 2000;
    });
    return candidates.map((account) => ({
        accountId: account.account_id,
        accountNumber: String(account.account_number),
        accountName: account.account_name || '',
    }));
}

/**
 * Escape a value for interpolation into HTML markup/attributes (SweetAlert
 * dialogs build their bodies as HTML strings). Account names are tenant/SIE
 * data and must never reach the DOM unescaped.
 */
export function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** Path to the role settings surface (Bokföring → Kontoplansroller). */
export function roleSettingsPath(companyId) {
    return `/${companyId}/settings/chart?vy=roller`;
}
